import { useEffect, useRef } from "react";
import { Loader2, MessageCircle, PartyPopper, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";

/**
 * The slide after the last question: what was done, what finishing is worth, and
 * one button that ends the session.
 *
 * It exists because the session had no ending of its own. „Pateikti" was a third
 * control in the bottom bar of the last question, sharing that bar with the
 * question's own navigation and — while the tutor had a conversation open about
 * that question — disabled behind a tooltip telling the child to go and finish
 * the chat. On any other question that refusal costs nothing: the conversation is
 * right there and the session continues. On the last one there is nothing after
 * it, so the only exits left were to guess wrong or to abandon the session, and
 * an abandoned session counts for nothing at all.
 *
 * So finishing is a place now, not a state of the last question. Reaching it is
 * never blocked — the conversation stays open behind it and „Atgal" returns to
 * it — which is the whole of the fix; everything else here is what a screen
 * whose only job is to end well should be carrying anyway.
 *
 * Deliberately not a second celebration. `SessionComplete` is the celebration and
 * it comes moments later; two in a row would make the first one mean nothing.
 *
 * The order is what is *actionable* first and what is merely *readable* last:
 * anything still open (the one thing the child can act on, and the reason the
 * button is grey), then what finishing pays, then the conversations, then the
 * questions. The question grid is the tallest block and the least urgent, so it
 * goes to the bottom — anything above it is above the fold on the screens this is
 * read on, and the reward is the reason to press.
 */

export type ReviewQuestion = {
  id: string;
  /** 1-based, as the child counts them. */
  number: number;
  status: "correct" | "partial" | "incorrect" | "skipped" | "pending" | "unanswerable";
  /** A conversation still open about this question. */
  talking: boolean;
  /**
   * This question still owes the tutor a conversation, under the optional rule a
   * grown-up switched on for this learner. Narrower than `talking`: a chat left
   * open after it was worked through owes nothing.
   */
  owing: boolean;
};

type PointsRules = {
  completion: number;
  correctAnswer: number;
  /** What a question got right with the tutor's help pays. */
  partialAnswer: number;
  perfectSession: number;
};

type Props = {
  questions: ReviewQuestion[];
  correctCount: number;
  partialCount: number;
  skippedCount: number;
  /** Answerable questions with neither a verdict nor a skip. Blocks finishing. */
  openCount: number;
  /**
   * Whether an unresolved conversation blocks finishing for this learner. Off
   * for everybody unless a grown-up said otherwise — see `DiscussionGate`.
   */
  requiresDiscussionResolution?: boolean;
  pointsRules?: PointsRules | null;
  /** Jump back to a question, 0-based slide index. */
  onGoTo: (slide: number) => void;
};

/**
 * What to say about the work, forwards rather than backwards: the child is about
 * to finish, not being marked. Never a verdict on them — the low branch praises
 * the thing they actually did, which is get to the end of it.
 */
function headline(
  correct: number,
  answered: number,
  open: number
): { emoji: string; title: string; line: string } {
  if (open > 0) {
    return {
      emoji: "👀",
      title: "Beveik!",
      line: open === 1 ? "Liko viena neatsakyta užduotis" : `Liko neatsakytų užduočių: ${open}`,
    };
  }
  if (answered > 0 && correct === answered) {
    return { emoji: "🏆", title: "Nė vienos pro šalį!", line: "Visos užduotys teisingai" };
  }
  const share = answered > 0 ? correct / answered : 0;
  if (share >= 0.8) {
    return { emoji: "🎉", title: "Šauniai padirbėta!", line: "Beveik viskas teisingai" };
  }
  if (share >= 0.5) {
    return { emoji: "💪", title: "Gerai padirbėta!", line: "Daugiau nei pusė užduočių teisingai" };
  }
  return { emoji: "🌟", title: "Nuėjai iki galo!", line: "Užduotys buvo sunkios — bet tu jų neapleidai" };
}

/** How many unfinished conversations are named before the rest become a count. */
const NAMED_CHATS = 4;

/**
 * „ir dar 4 kitas" — the tail of the named list, with the noun kept on it.
 *
 * A bare „ir dar 4" after „4, 9, 10, 12" reads as a fifth question number, which
 * is the one thing this sentence must not be ambiguous about. Lithuanian numeral
 * agreement, so the noun can actually be there: 11–19 and 0 take the genitive
 * plural, a final 1 the accusative singular, everything else the accusative
 * plural (agreeing with „užduotis").
 */
function andMoreQuestions(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (lastTwo >= 11 && lastTwo <= 19) return `ir dar ${count} kitų`;
  if (last === 0) return `ir dar ${count} kitų`;
  if (last === 1) return `ir dar ${count} kitą`;
  return `ir dar ${count} kitas`;
}

const DOT: Record<ReviewQuestion["status"], string> = {
  correct: "bg-green-500 text-white border-green-500",
  partial: "bg-yellow-500 text-white border-yellow-500",
  incorrect: "bg-red-500 text-white border-red-500",
  // Neither right nor wrong, so not a colour — the same reading the progress
  // rail gives a skip.
  skipped: "bg-muted-foreground/60 text-background border-transparent",
  pending: "bg-background text-muted-foreground border-dashed border-muted-foreground/60",
  unanswerable: "bg-muted text-muted-foreground border-transparent",
};

/**
 * The one button, in the bar the child's hand is already on.
 *
 * It sits in the page's fixed bottom bar rather than at the end of the summary
 * above: twenty-five questions push a button placed after them below the fold, and
 * a focused primary action nobody can see is not a focused primary action. There
 * is exactly one of it — repeating it in the body would make whichever copy the
 * child ignores read as decoration.
 */
export function SessionFinishButton({
  isLearner,
  isSubmitting,
  canSubmit,
  onSubmit,
}: {
  isLearner: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
}) {
  // Enter finishes. The child arrives here from the last question — pressed
  // „Toliau", or carried by a correct answer — and the one thing this screen is
  // for should not need finding with a mouse. Safe to focus a submit: it is
  // `disabled` while anything is still open, so an Enter left over from checking
  // the last answer cannot hand in an unfinished session.
  //
  // `preventScroll`, because the slide it is arriving on has just asked the
  // window to scroll to the top.
  //
  // Retried once a frame later, and only while nothing else owns the keyboard:
  // the slide being left behind is unmounting a focused answer input in the same
  // commit, and a focus set while that is still happening can be handed straight
  // back to the document. If the child has meanwhile focused something for
  // themselves — „Atgal", a question in the grid — that outranks this and the
  // retry stands down.
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!canSubmit) return;
    ref.current?.focus({ preventScroll: true });
    const again = requestAnimationFrame(() => {
      const button = ref.current;
      if (!button || button.disabled) return;
      const active = document.activeElement;
      if (active && active !== document.body && active !== document.documentElement) return;
      button.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(again);
  }, [canSubmit]);

  return (
    <Button
      ref={ref}
      onClick={onSubmit}
      disabled={!canSubmit || isSubmitting}
      size="lg"
      className="h-12 flex-1 rounded-xl text-base font-bold sm:flex-none sm:px-10"
    >
      {isSubmitting ? (
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
      ) : (
        <PartyPopper className="mr-2 h-5 w-5" />
      )}
      {isLearner ? "Baigiu!" : "Pateikti atsakymus"}
    </Button>
  );
}

export function SessionReview({
  questions,
  correctCount,
  partialCount,
  skippedCount,
  openCount,
  requiresDiscussionResolution = false,
  pointsRules,
  onGoTo,
}: Props) {
  const answered = correctCount + partialCount + questions.filter((q) => q.status === "incorrect").length;
  const { emoji, title, line } = headline(correctCount, answered, openCount);
  const talking = questions.filter((q) => q.talking);
  const owing = requiresDiscussionResolution ? questions.filter((q) => q.owing) : [];
  // A question the gate above is already naming must not also appear here, or
  // the child reads two notices about one conversation — and the second one ends
  // „gali baigti ir dabar", which is exactly what the first says they cannot.
  const advisory = talking.filter((q) => !owing.includes(q));
  const firstOpen = questions.find((q) => q.status === "pending");

  // The floor, never a promise: the streak bonus, personal bests, unlocks and
  // achievements all add to it and none of them is knowable from here. Hence
  // „bent".
  const forecast = pointsRules
    ? {
        total:
          pointsRules.completion +
          correctCount * pointsRules.correctAnswer +
          partialCount * pointsRules.partialAnswer +
          (answered > 0 && correctCount === answered ? pointsRules.perfectSession : 0),
        perfect: answered > 0 && correctCount === answered,
      }
    : null;

  return (
    <div className="mx-auto w-full max-w-lg space-y-4">
      <div className="relative overflow-hidden rounded-3xl border bg-gradient-to-br from-sky-400/10 via-violet-500/10 to-emerald-400/10 dark:from-sky-400/15 dark:via-violet-500/15 dark:to-emerald-400/15">
        <div className="pointer-events-none absolute -top-24 -right-20 h-56 w-56 rounded-full bg-gradient-to-br from-sky-400/25 to-violet-400/25 blur-3xl" />
        <div className="relative flex flex-col items-center gap-1.5 px-6 py-7 text-center">
          <span className="text-5xl leading-none">{emoji}</span>
          <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{title}</h2>
          <p className="text-sm text-muted-foreground">{line}</p>

          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            <span className="rounded-full bg-background/70 px-3 py-1.5 text-sm font-semibold shadow-sm backdrop-blur-sm">
              ✅ {correctCount} teisingai
            </span>
            {partialCount > 0 && (
              <span className="rounded-full bg-background/70 px-3 py-1.5 text-sm font-semibold shadow-sm backdrop-blur-sm">
                ➗ {partialCount} iš dalies
              </span>
            )}
            {skippedCount > 0 && (
              <span className="rounded-full bg-background/70 px-3 py-1.5 text-sm font-semibold shadow-sm backdrop-blur-sm">
                ⏭️ {skippedCount} praleista
              </span>
            )}
          </div>
        </div>
      </div>

      {/* What is still open comes first: it is the only thing on this screen the
          child can still act on, and it is why the button below is grey. */}
      {openCount > 0 && (
        <div className="rounded-2xl border border-amber-400/50 bg-amber-50 p-4 dark:border-amber-400/30 dark:bg-amber-500/10">
          <p className="text-sm font-medium">
            {openCount === 1
              ? "Viena užduotis dar neatsakyta."
              : `Dar neatsakytos ${openCount} užduotys.`}{" "}
            Atsakyk arba praleisk ją, ir galėsi baigti.
          </p>
          {firstOpen && (
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => onGoTo(firstOpen.number - 1)}
            >
              Eiti į {firstOpen.number} užduotį
            </Button>
          )}
        </div>
      )}

      {/* The other reason the button is grey, and it only exists for the learners
          a grown-up switched the rule on for. It sits with the unanswered-question
          notice rather than with the conversations note below, because those are
          two different kinds of sentence: one says what is stopping you, the
          other says what going back is worth.

          It says what „aptarti" actually takes — write back once or twice — so it
          is a thing to do rather than a wall. The child cannot be trapped behind
          it: two of their own turns satisfy it whatever the tutor does. */}
      {owing.length > 0 && (
        <div className="rounded-2xl border border-amber-400/50 bg-amber-50 p-4 dark:border-amber-400/30 dark:bg-amber-500/10">
          <p className="text-sm font-medium">
            {owing.length === 1
              ? `Klaidingą atsakymą (${owing[0].number} užduotis) dar reikia aptarti pokalbyje.`
              : `Klaidingus atsakymus dar reikia aptarti pokalbyje: ${owing
                  .slice(0, NAMED_CHATS)
                  .map((q) => q.number)
                  .join(", ")}${
                  owing.length > NAMED_CHATS
                    ? ` ${andMoreQuestions(owing.length - NAMED_CHATS)}`
                    : ""
                }.`}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Parašyk pokalbyje, kaip galvojai — nebūtina iškart teisingai. Tada galėsi baigti.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => onGoTo(owing[0].number - 1)}
          >
            {owing.length === 1 ? "Grįžti į pokalbį" : `Grįžti į ${owing[0].number} užduotį`}
          </Button>
        </div>
      )}

      {/* Then the reward, high enough to be read without scrolling: it is the
          reason to press the button, so it must not sit under the long list. */}
      {forecast && (
        <div className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-100/70 to-yellow-50 p-4 dark:border-amber-400/25 dark:from-amber-500/10 dark:to-yellow-500/5">
          <p className="flex items-center gap-2 text-sm font-bold text-amber-800 dark:text-amber-200">
            <Sparkles className="h-4 w-4" />
            Užbaigus gausi bent +{forecast.total} taškų
          </p>
          <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
            {forecast.perfect
              ? "Sesijos užbaigimas, teisingi atsakymai ir premija už tobulą sesiją."
              : "Sesijos užbaigimas ir teisingi atsakymai."}{" "}
            Serija ir pasiekimai pridės dar.
          </p>
        </div>
      )}

      {/* An open conversation is a note, never a gate. It was a gate, and on the
          last question that made the session impossible to hand in.

          So it has to be worth reading rather than worth obeying, which means
          saying what going back is *for* — in the one currency a child can weigh
          it in: a question worked out in the chat is scored PARTIAL and paid for,
          where one left open stays INCORRECT and is paid nothing. That is the
          same sentence the note under „Toliau" makes, on the screen where the
          choice is actually being made.

          Nothing here names the thing on the other side of the chat. It is not a
          „mokytojas" — there is no teacher in this app — and „pokalbis" is the
          only noun the child needs: it is the word on the button that opened it. */}
      {advisory.length > 0 && (
        <div className="rounded-2xl border bg-card p-4">
          <div className="flex items-start gap-2">
            <MessageCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-500" />
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {advisory.length === 1
                  ? `Neužbaigtas pokalbis apie ${advisory[0].number} užduotį.`
                  : /* Named, but not all of them: the point is to say which one to
                       go back to, and a list of twelve numbers is a list nobody
                       reads. The grid below badges every one of them. */
                    `Neužbaigti pokalbiai apie užduotis: ${advisory
                      .slice(0, NAMED_CHATS)
                      .map((q) => q.number)
                      .join(", ")}${
                      advisory.length > NAMED_CHATS
                        ? ` ${andMoreQuestions(advisory.length - NAMED_CHATS)}`
                        : ""
                    }.`}
              </p>
              <p className="text-sm text-muted-foreground">
                {pointsRules?.partialAnswer
                  ? advisory.length === 1
                    ? `Išsiaiškink ją iki galo — gausi už ją ${pointsRules.partialAnswer} taškus. Palikta neišsiaiškinta taškų neduos.`
                    : `Kiekviena išsiaiškinta užduotis duos ${pointsRules.partialAnswer} taškus. Paliktos neišsiaiškintos taškų neduos.`
                  : advisory.length === 1
                    ? "Išsiaiškinta užduotis skaitoma iš dalies atlikta, o palikta neišsiaiškinta — ne."
                    : "Išsiaiškintos užduotys skaitomos iš dalies atliktos, o paliktos neišsiaiškintos — ne."}
              </p>
              <p className="text-xs text-muted-foreground">
                Bet gali baigti ir dabar — tavo sprendimas.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => onGoTo(advisory[0].number - 1)}
          >
            {advisory.length === 1
              ? "Grįžti į pokalbį"
              : `Grįžti į ${advisory[0].number} užduotį`}
          </Button>
        </div>
      )}

      {/* Every question at a glance, and each one is the way back to itself. The
          rail at the top of a question does the same job in two-and-a-half
          pixels; here there is room for the number, which is what a child asks
          for („kur buvo ta septinta?"). Last, because it is the biggest block on
          the screen and the least urgent thing on it. */}
      <div className="rounded-2xl border bg-card p-4">
        <p className="mb-3 text-sm font-medium text-muted-foreground">Tavo užduotys</p>
        <div className="flex flex-wrap gap-2">
          {questions.map((question) => (
            <button
              key={question.id}
              type="button"
              onClick={() => onGoTo(question.number - 1)}
              aria-label={`Grįžti į ${question.number} užduotį`}
              className={`relative h-9 w-9 rounded-xl border text-sm font-semibold transition-transform hover:scale-110 ${DOT[question.status]}`}
            >
              {question.number}
              {question.talking && (
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-500 text-[9px] text-white">
                  <MessageCircle className="h-2.5 w-2.5" />
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
